import { ReportChapterObject } from '../../base';
// Import necessary case study fixtures
import * as CaseStudies from '../../content/case-study';
import { createScores } from '../../base/scoreHelper'; // Keep if needed for external sources

// Define the updated chapter object
export const technicalImplementationsTesting: Omit<ReportChapterObject, 'number'> = {
    id: 'automated-unit-testing', // Keep existing ID
    caption: `Playbook: Automated Testing`, // Keep existing caption
    // Refined title focusing on results
    title: `AI Test Automation Delivers High Code Coverage and Reduces Manual Effort`,
    // Refined description focusing on results and outcomes
    description: `Automated unit testing using multi-agent AI yields substantial results, including dramatically increased code coverage (from 3% to 50% in legacy code examples) and near-instantaneous test generation. Systems implemented by companies like Uber and Capgemini significantly reduce developer toil by automating complex testing processes, improve test reliability through iterative feedback and validation like mutation testing, and ultimately enhance overall code quality and maintainability.`,
    editedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
    // Replace existing sections with new content based on DETAILS 1-5 and Conclusion
    sections: [
        // Section 1: System Architecture and Agent Workflows
        {
            id: 'architecture-workflows',
            title: `Multi-Agent Pipelines Coordinate Specialized Testing Tasks`,
            caption: `Architecture`,
            content: [
                {
                    kind: 'text',
                    text: 'Both Uber and Capgemini implement a multi-agent pipeline that coordinates various tasks in sequential or iterative steps. Each “agent” is designed to handle a specific subset of responsibilities, from preparing the environment to validating that newly generated tests meet coverage and style requirements.',
                },
                {
                    kind: 'heading',
                    text: 'Core Agents (Uber “AutoCover” Example):',
                    level: 3,
                },
                {
                    kind: 'heading',
                    text: 'Preparer Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Creates scaffolding for test files.\nEnsures environment variables (specific to Uber’s internal build systems) are set.\nGathers any relevant dependencies (imports, build settings).',
                },
                {
                    kind: 'heading',
                    text: 'Generator Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Leverages a large language model (LLM) to produce initial test code.\nWorks off prompts that include context such as function signatures, existing code, or partial scaffolding from the Preparer.',
                },
                {
                    kind: 'heading',
                    text: 'Executor Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Integrates the candidate tests into a local working copy of the codebase.\nTriggers syntax checks, compilation, and test runs.\nRecords which tests pass/fail, captures compiler or runtime errors.',
                },
                {
                    kind: 'heading',
                    text: 'Fixer Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Takes feedback from the Executor (e.g., missing imports, type mismatches, failing assertions) and re-invokes the LLM with that error data.\nAttempts to patch the code to resolve the issues, re-inserting corrected snippets.',
                },
                {
                    kind: 'heading',
                    text: 'Refactor Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Restructures or merges the test cases (e.g., converting multiple small tests into table-driven tests).\nApplies internal style conventions or merges duplicate logic.',
                },
                {
                    kind: 'heading',
                    text: 'Validator Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Verifies coverage thresholds, presence of meaningful assertions, and alignment with style guidelines.\nCan use a deterministic checker (looking for “assert” statements) and a second pass with a “probabilistic LLM critic” to gauge test logic quality.',
                },
                {
                    kind: 'heading',
                    text: 'Core Agents (Capgemini Example):',
                    level: 3,
                },
                {
                    kind: 'text',
                    text: `Similarly uses Generator, Fixer, and Refactor phases, but emphasizes:`,
                },
                {
                    kind: 'heading',
                    text: 'Class-Level Breakdown',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Each Java class is handled in isolation to avoid context overflow and to systematically raise coverage across large codebases.`,
                },
                {
                    kind: 'heading',
                    text: 'Document AI',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `(parallel to test generation) – Not strictly for unit testing but used for generating domain-specific documentation from code.`,
                },
                {
                    kind: 'heading',
                    text: 'Private Infrastructure',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Runs entirely on a local or on-prem environment, ensuring code never leaves the client’s secure perimeter.`,
                },
                {
                    kind: 'heading',
                    text: 'Deterministic vs. Probabilistic Components',
                    level: 3,
                },
                {
                    kind: 'text',
                    text: `A major technical differentiator is the marriage of deterministic tools (e.g., compilers, linters, coverage analyzers) with probabilistic LLM outputs:`,
                },
                {
                    kind: 'heading',
                    text: 'Deterministic Tools',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Provide unambiguous feedback (e.g., “Compilation failed due to missing import”). The system captures these exact errors to form the next prompt for the LLM.`,
                },
                {
                    kind: 'heading',
                    text: 'Probabilistic LLM',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Generates test cases or fixes in code format. The Fixer Agent may need multiple LLM calls to refine code until it passes strict deterministic checks.`,
                },
                {
                    kind: 'divider',
                },
                {
                    kind: 'text',
                    text: `This approach ensures that each step (generate, integrate, run, fix) is driven by hard data—like logs, compilation errors, or coverage deltas—rather than purely human guesswork.`,
                },
            ],
            keywords: [], // Set to empty array
            sources: [CaseStudies.uberAutocoverCaseStudy, CaseStudies.capgeminiCaseStudy],
        },
        // Section 2: Key Technical Elements
        {
            id: 'key-technical-elements',
            title: `Environment Prep, Prompting, and Feedback Loops Drive Test Quality`,
            caption: `Core Techniques`,
            content: [
                {
                    kind: 'heading',
                    text: `Environment Preparation and Context Injection`,
                    level: 3,
                },
                {
                    kind: 'heading',
                    text: 'Environment Setup (Uber “AutoCover”)',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'AutoCover must integrate seamlessly with Uber’s large, custom build systems.\n\nThe Preparer Agent:\n\nInjects environment variables (e.g., “UBER_BUILD_CONFIG”) so the LLM sees how the code is compiled.\n\nCombines “surrounding context” (like import statements, existing directory structure) into a prompt-friendly format.',
                },
                {
                    kind: 'heading',
                    text: 'Class Isolation (Capgemini)',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Each Java class is packaged with a minimal set of relevant references: the class definition, any associated interfaces, or references to the immediate test environment.\n\nThis addresses LLM context-window limits. Code spanning millions of lines is chunked so the agent only sees the portion needed.`,
                },
                {
                    kind: 'heading',
                    text: `Prompt Engineering for Test Generation`,
                    level: 3,
                },
                {
                    kind: 'heading',
                    text: 'Generator Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Receives a structured prompt including:\n\nFunction signatures or relevant lines to be tested (Uber references the user selecting lines 18–26 to test).\n\nBuild or style guidelines (e.g., “use JUnit 5,” “use table-driven approach,” “use try/catch for coverage”).\n\nExample patterns from validated tests (helps the LLM mimic correct naming conventions).',
                },
                {
                    kind: 'heading',
                    text: 'Parsing Output',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'The Agent extracts only the test code portion from the LLM’s response.\n\nImplementation detail: Some solutions parse code fenced within triple-backticks (“java ... ”) or specific delimiters.\n\nNon-code content (e.g., explanations) is ignored or logged for debugging.',
                },
                {
                    kind: 'heading',
                    text: 'Execution and Automated Feedback',
                    level: 3,
                },
                {
                    kind: 'heading',
                    text: 'Executor Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Splices the newly generated test code into an actual test file:\n\nPossibly appended to an existing file, or it creates a brand-new test suite.\n\nCompiles and runs the test suite using existing build pipelines (e.g., Gradle, Maven, Bazel).\n\nCaptures:\n\nCompiler errors (missing symbols, type errors).\n\nTest failures (exceptions, assertion mismatches).\n\nCoverage reports (class or method coverage, line-level stats).',
                },
                {
                    kind: 'heading',
                    text: 'Continuous Integration Integration',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Both systems can operate in dev and CI environments.\n\nAt Uber, nightly or on-demand runs can process hundreds of files.\n\nAt Capgemini, local runs on a developer’s laptop achieve near-instant feedback for incremental test generation.',
                },
                {
                    kind: 'heading',
                    text: 'Error Correction and Iterative Loops',
                    level: 3,
                },
                {
                    kind: 'heading',
                    text: 'Fixer Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Takes structured error logs from the Executor.\n\nFor instance, “Method ‘getVersionP’ not found, did you mean ‘getVersion’?”\n\nRe-invokes the LLM with a system prompt referencing the previous generated test code plus any relevant sections from the original class.\n\nUpdates references to fix the mismatch or fill missing imports.',
                },
                {
                    kind: 'heading',
                    text: 'Iteration Count',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'The process repeats until:\n\nCompilation passes with zero errors.\n\nAll tests run without throwing uncaught exceptions.\n\nCoverage or “test quality” thresholds are met (mutation tests, style checks).',
                },
                {
                    kind: 'text',
                    text: 'Example:\n\nIf a method was renamed from versionP to version in the source code, but the test used the old name, the Fixer sees the “symbol not found” error, instructs the LLM to correct references, and re-runs.',
                },
                {
                    kind: 'heading',
                    text: 'Refactoring and Validating Final Tests',
                    level: 3,
                },
                {
                    kind: 'heading',
                    text: 'Refactor Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Aggregates multiple small or partially redundant test cases into a consolidated structure (e.g., a “table test” pattern).\n\nRemoves leftover or commented-out code from repeated fix attempts.\n\nIn the Capgemini approach, also addresses style issues (long methods, unhandled exceptions) by splitting them into smaller methods.',
                },
                {
                    kind: 'heading',
                    text: 'Validator Agent',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Runs static/deterministic checks:\n\nConfirms each test includes at least one assertion.\n\nChecks coverage thresholds (e.g., “must exceed 80% class coverage”).\n\nVerifies naming conventions, coding style, required docstrings.\n\nMay optionally do a final LLM pass (the “critic”) to see if tests appear logically complete (e.g., “Are we testing all relevant paths in function X?”).\n\nIf validation fails, it pushes the code back to either the Fixer or the Refactor Agent.',
                },
                {
                    kind: 'heading',
                    text: 'Mutation Testing for Quality Assurance',
                    level: 3,
                },
                {
                    kind: 'heading',
                    text: 'Uber’s AutoCover',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Introduces “mutants” (intentional micro-changes in the production code).\n\nIf the tests fail to detect these changes, the tests are flagged for additional improvement.\n\n“If your tests don’t catch that, then there’s a problem.”',
                },
                {
                    kind: 'heading',
                    text: 'Capgemini',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Although not explicitly spelled out as mutation testing, the same principle applies: if a generated test fails in a suspicious or trivial scenario, the system prompts an additional iteration to strengthen coverage.',
                },
            ],
            keywords: [], // Set to empty array
            sources: [CaseStudies.uberAutocoverCaseStudy, CaseStudies.capgeminiCaseStudy],
        },
        // Section 3: Data Flows and Integration Points
        {
            id: 'data-flows-integration',
            title: `IDE and CI Integration Streamline Test Generation Workflows`,
            caption: `Integration`,
            content: [
                {
                    kind: 'heading',
                    text: 'IDE Integration',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Uber’s solution is deeply integrated into VS Code, where developers can highlight a specific code block. The Generator is invoked to produce tests targeting precisely those lines.\n\nCapgemini’s approach can also run from a command-line interface or a minimal UI, especially for large-scale batch conversions.',
                },
                {
                    kind: 'heading',
                    text: 'Continuous Integration Hooks',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Both pipelines can be triggered by commit hooks or nightly tasks.\n\nAutomation is essential to handle large volumes (hundreds of files at Uber, “millions of lines” at Capgemini).',
                },
                {
                    kind: 'heading',
                    text: 'Security & Privacy',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Capgemini runs everything on a local or private GPU-enabled instance. “Zero problems with data privacy.”\n\nUber references either specialized internal endpoints or a secure environment so that code never leaves their network.',
                },
            ],
            keywords: [], // Set to empty array
            sources: [CaseStudies.uberAutocoverCaseStudy, CaseStudies.capgeminiCaseStudy],
        },
        // Section 4: Performance Observations
        {
            id: 'performance-observations',
            title: `AI Pipelines Achieve High Coverage and Rapid Iteration Speeds`,
            caption: `Performance`,
            content: [
                {
                    kind: 'heading',
                    text: 'Coverage Achievements',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Uber: Demonstrated near-instant 100% coverage for a small, chosen file in a live demo.\nCapgemini: Ramped up from 3% coverage in large legacy code to 50% coverage in tested modules.',
                },
                {
                    kind: 'heading',
                    text: 'Iteration Speed',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'With local or in-network operation, each iteration loop (generate → compile → run → fix) can occur in a matter of seconds.\nFor large-scale “batch” conversions, times vary, but the pipeline can run overnight to update hundreds of files.',
                },
                {
                    kind: 'heading',
                    text: 'Quality & Reliability',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Deterministic feedback ensures that any erroneous test generation is quickly flagged and fixed.\nMutation testing uncovers brittle or incomplete tests, prompting the LLM to refine coverage.',
                },
            ],
            keywords: [], // Set to empty array
            sources: [CaseStudies.uberAutocoverCaseStudy, CaseStudies.capgeminiCaseStudy],
        },
        // Section 5: Future Extensions
        {
            id: 'future-extensions',
            title: `Expanding Context Windows and Agentic Consultation Offer Future Potential`,
            caption: `Future`,
            content: [
                {
                    kind: 'heading',
                    text: 'Enhanced Context Windows',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'As LLM context limits expand, the system may ingest entire directories or modules at once, producing more integrated test suites.',
                },
                {
                    kind: 'heading',
                    text: 'Agentic “Consultation”',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Uber plans to let devs query an agent for clarifications when test generation gets stuck—blending human instructions into the multi-agent chain.',
                },
                {
                    kind: 'heading',
                    text: 'Integration with Additional Tooling',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Potential to incorporate code coverage dashboards, JIRA ticket auto-creation for failed validations, or “AI-based code lens” in the IDE to highlight coverage gaps.',
                },
            ],
            keywords: [], // Set to empty array
            sources: [CaseStudies.uberAutocoverCaseStudy], // Primarily Uber mentioned here
        },
        // Section 6: Conclusion
        {
            id: 'testing-conclusion',
            title: `Multi-Agent Architecture with Deterministic Feedback Enables Robust Test Automation`,
            caption: `Summary`,
            content: [
                {
                    kind: 'text',
                    text: 'These AI-driven unit-test generation systems, exemplified by Uber’s AutoCover and Capgemini’s Java legacy approach, rely on a multi-agent architecture that combines deterministic validation with LLM-powered test creation. Each agent performs a narrow yet critical function—from scaffolding the environment to resolving compilation errors—while tight feedback loops ensure that every test produced is both syntactically correct and contextually meaningful.',
                },
                {
                    kind: 'heading',
                    text: 'Agentic Chain',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Breaking tasks into specialized agents keeps the pipeline manageable and modular.',
                },
                {
                    kind: 'heading',
                    text: 'Context Management',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Class-level or file-level context is essential to avoid LLM confusion on massive codebases.',
                },
                {
                    kind: 'heading',
                    text: 'Deterministic + LLM',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Compiler errors, lint outputs, and coverage stats provide a robust “ground truth” for the LLM to iterate toward.',
                },
                {
                    kind: 'heading',
                    text: 'Security & Privacy',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Running on private infrastructure ensures enterprise compliance and fosters adoption in highly regulated environments.',
                },
                {
                    kind: 'divider',
                },
                {
                    kind: 'text',
                    text: 'By automating once-manual, multi-step processes and enforcing rigorous checks at each stage, these solutions yield dramatic improvements in code coverage and significantly reduce the manual burden of writing and maintaining high-quality unit tests.',
                },
            ],
            keywords: [], // Set to empty array
            sources: [CaseStudies.uberAutocoverCaseStudy, CaseStudies.capgeminiCaseStudy],
        },
    ],
};
